import React, { useState, useRef, useEffect } from 'react';
import '../styles/components/FilterPanel.css';
import FilterGroup, { FilterGroupRef } from './FilterGroup';
import Filter from './Filter';
import { FilterConfig, ResourceFilterConfig } from '../types/filterTypes';

interface FilterPanelProps {
  filters: Record<string, any>;
  filterConfig: ResourceFilterConfig;
  onFilterChange: (filters: Record<string, any>) => void;
  onClose: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({ 
  filters, 
  filterConfig,
  onFilterChange,
  onClose
}) => {
  const [localFilters, setLocalFilters] = useState<Record<string, any>>(filters);

  // Use refs to access FilterGroup validation methods
  const filterGroupRefs = useRef<{[key: string]: React.MutableRefObject<FilterGroupRef | null>}>({});

  // Initialize refs for each group
  useEffect(() => {
    const groups = filterConfig.groups || [];
    const currentRefs = filterGroupRefs.current;
    groups.forEach(group => {
      if (!currentRefs[group.id]) {
        currentRefs[group.id] = { current: null };
      }
    });
  }, [filterConfig.groups]);

  //Update local filters when props change
  useEffect(() => {
    setLocalFilters({...filters});
  }, [filters]);


  const handleFilterUpdate = (key: string, value: any) => {
    setLocalFilters(prev => {
      const newFilters = { ...prev };
      if (value === '' || value === null || value === undefined) {
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
      return newFilters;
    });
  };

  const handleSubmit = () => {
    onFilterChange(localFilters);
    onClose();
  };

  const handleReset = () => {
    // Reset to defaults from config
    const defaults: Record<string, any> = {};
    filterConfig.filters.forEach(filter => {
      if (filter.defaultValue !== undefined) {
        defaults[filter.key] = filter.defaultValue;
      }
    });
    setLocalFilters(defaults);
  };

  // Render a filter based on its configuration
  const renderFilter = (filterConfig: FilterConfig) => {
    const { key, type, label, placeholder, isMultiValue, options } = filterConfig;
    const value = localFilters[key] ?? '';

    return (
      <Filter
        key={key}
        label={label}
        type={type}
        value={value}
        onChange={(newValue) => handleFilterUpdate(key, newValue)}
        placeholder={placeholder}
        options={options}
        isMultiValue={isMultiValue}
        filterKey={key}
      />
    );
  };

  // Group filters
  const getGroupedFilters = () => {
    const { filters: filterDefs, groups = [] } = filterConfig;
    
    // Create a map of group IDs to filters
    const groupMap: Record<string, FilterConfig[]> = {};
    
    // Initialize group map and set of special keys to exclude from rendering
    groups.forEach(group => {
      groupMap[group.id] = [];
    });
    
    // Add ungrouped as a special case
    groupMap['ungrouped'] = [];
    
    // Set of keys that should be excluded from regular rendering
    // because they're handled by FilterGroup
    const excludeKeys = new Set<string>();
    
    // Add all of the group-specific keys to the exclude set
    groups.forEach(group => {
      excludeKeys.add(group.id); // General filter key
      excludeKeys.add(`${group.id}_min`); // Min filter key
      excludeKeys.add(`${group.id}_max`); // Max filter key
    });
    
    // Assign filters to groups, skipping the ones that are handled by FilterGroup
    filterDefs.forEach(filter => {
      // Skip filters that are handled by FilterGroup components
      if (excludeKeys.has(filter.key)) {
        return;
      }
      
      const groupId = filter.groupId || 'ungrouped';
      if (!groupMap[groupId]) {
        groupMap[groupId] = [];
      }
      
      groupMap[groupId].push(filter);
    });
    
    return { groupMap, groups };
  };

  const { groupMap, groups } = getGroupedFilters();

  const handleApplyFilters = () => {
    console.log('Apply button clicked, validating filters...');
    let isValid = true;
    
    // Check all group filters for validation
    console.log('Filter refs:', filterGroupRefs.current);
    
    Object.entries(filterGroupRefs.current).forEach(([key, ref]) => {
      console.log(`Validating group: ${key}`, ref);
      if (ref.current) {
        const groupIsValid = ref.current.validate();
        console.log(`Group ${key} validation result:`, groupIsValid);
        if (!groupIsValid) {
          isValid = false;
        }
      } else {
        console.warn(`Ref for group ${key} is null!`);
      }
    });
    
    console.log('Overall validation result:', isValid);
    
    if (isValid) {
      // Only apply filters if all validations pass
      onFilterChange(localFilters);
      onClose();
    }
  };

  return (
    <div className="filter-panel">
      <div className="filter-panel-header">
        <h3>Filter {filterConfig.resourceName}</h3>
        <button className="close-button" onClick={onClose}>
          <span className="material-icons">close</span>
        </button>
      </div>
      
      <div className="filter-panel-content">
        {/* Render grouped filters */}
        {groups.map(group => {
          // Ensure ref is created for this group
          if (!filterGroupRefs.current[group.id]) {
            filterGroupRefs.current[group.id] = { current: null };
          }
          
          return (
            <FilterGroup
              key={group.id}
              ref={filterGroupRefs.current[group.id]}
              groupName={group.label}
              generalFilter={{
                key: group.id,
                value: localFilters[group.id] || '',
                placeholder: `Enter ${group.label.toLowerCase()}`
              }}
              minMaxFilters={{
                minKey: `${group.id}_min`,
                maxKey: `${group.id}_max`,
                minValue: localFilters[`${group.id}_min`] || '',
                maxValue: localFilters[`${group.id}_max`] || '',
                minLabel: `From`,
                maxLabel: `To`,
                type: group.id.toLowerCase().includes('date') ? 'date' : 
                      group.id.toLowerCase().includes('time') ? 'time' : 'number'
              }}
              onFilterChange={handleFilterUpdate}
            >
              {/* Only render additional filters that belong to this group,
                 not the main/min/max ones already handled by FilterGroup */}
              {/* {groupMap[group.id].map(filter => renderFilter(filter))} */}
            </FilterGroup>
          );
        })}
        
        {/* Render ungrouped filters */}
        <div className='filter-row '>
        {groupMap['ungrouped'].map(filter => renderFilter(filter))}
        </div>
      </div>
      
      <div className="filter-panel-footer">
        <button 
          className="reset-button"
          onClick={handleReset}
        >
          Reset
        </button>
        <button 
          className="apply-button"
          onClick={handleApplyFilters}
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
};

export default FilterPanel; 